import React from 'react';

import { Box, Typography } from '@mui/material';

import StandaloneMultiProductOptionSelect from '~/components/PromoV3/StandaloneMultiProductOptionSelect';

import { PromoProductTypes, Voucher } from '~/services/PromoService';

export type VoucherProductType = NonNullable<Voucher['productType']>;

export const VOUCHER_PRODUCT_TYPE_OPTIONS: ReadonlyArray<VoucherProductType> = [
  'flight',
  'generic',
  'hotel',
  'cruise',
  'tour',
  'carhire',
];

const CATEGORY_TO_PRODUCT_TYPE: Readonly<Record<string, VoucherProductType>> = {
  carhire: 'carhire',
  cruise: 'cruise',
  flight: 'flight',
  hotel: 'hotel',
  tour: 'tour',
};

/**
 * Single `product_type` for the voucher API, from the first reward’s product line codes
 * (same `category_bk-…` values as the Rewards / detail-page multi-select).
 * Uses `generic` when the selection is empty or spans more than one category.
 */
export function inferVoucherProductTypeFromProductCodes(products: Array<string>): VoucherProductType {
  if (!products.length) {
    return 'generic';
  }
  const categories = new Set<string>();
  for (const p of products) {
    const i = p.indexOf('-');
    if (i <= 0) {
      continue;
    }
    categories.add(p.slice(0, i).toLowerCase());
  }
  if (categories.size !== 1) {
    return 'generic';
  }
  const [only] = categories;
  return only ? (CATEGORY_TO_PRODUCT_TYPE[only] ?? 'generic') : 'generic';
}

export type VoucherProductTypeFieldProps = {
  promoProductTypes: PromoProductTypes;
  value: Array<string>;
  onChange: (value: Array<string>) => void;
  disabled?: boolean;
  showAdvanceDescription?: boolean;
  id?: string;
};

/**
 * Multi-select of promo product lines — same control pattern as the voucher detail “Rewards” grid
 * (`StandaloneMultiProductOptionSelect` / `ProductMultiSelectEditCell`), bound to a reward’s `products`.
 */
export default function VoucherProductTypeField({
  promoProductTypes,
  value,
  onChange,
  disabled = false,
  showAdvanceDescription,
  id = 'voucher-product-type-field-products',
}: VoucherProductTypeFieldProps) {
  return (
    <Box>
      <StandaloneMultiProductOptionSelect
        productContext="voucher"
        id={id}
        value={value}
        promoProductTypes={promoProductTypes}
        multiSelectType="products"
        disabled={disabled}
        onChange={onChange}
      />
      {showAdvanceDescription ? (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Same control as the Products column in Rewards. Edits the first reward; on save, voucher product type is
          inferred (one category → that type, mixed → generic).
        </Typography>
      ) : null}
    </Box>
  );
}
